import React from 'react';

/**
 * MultiCheckboxSelect
 * @param {string} label
 * @param {Array<{ id: number|string, designation: string }>} options
 * @param {Array<number|string>} value
 * @param {function} onChange
 * @param {string} [className]
 */
const MultiCheckboxSelect = ({ label, options, value, onChange, className = '' }) => {
  const handleToggle = (id) => {
    const idStr = String(id);
    const valueStrs = value.map(String);
    if (valueStrs.includes(idStr)) {
      // Deselect
      onChange(value.filter(v => String(v) !== idStr));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className="font-extrabold text-xl mb-5 tracking-wide text-primary-700 dark:text-primary-400 flex items-center gap-2">
          <i className="fas fa-layer-group text-primary-500 text-2xl" />
          {label}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {options.map(opt => (
          <div
            key={opt.id}
            tabIndex={0}
            className={
              `group flex items-center gap-3 px-5 py-4 rounded-2xl border-2 cursor-pointer transition-all min-w-[180px] relative overflow-hidden bg-white dark:bg-gray-900 shadow-md hover:shadow-xl hover:border-primary-500 dark:hover:border-primary-400` +
              (value.map(String).includes(String(opt.id))
                ? ' border-green-500 ring-2 ring-green-200 dark:ring-green-700 bg-green-50 dark:bg-green-900/30 shadow-green-200 dark:shadow-green-900'
                : ' border-gray-200 dark:border-gray-700')
            }
            style={{
              boxShadow: value.map(String).includes(String(opt.id))
                ? '0 4px 24px 0 rgba(34,197,94,0.18), 0 1.5px 6px 0 rgba(34,197,94,0.10)'
                : '0 2px 8px 0 rgba(0,0,0,0.04)'
            }}
            onClick={() => handleToggle(opt.id)}
            onKeyDown={e => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleToggle(opt.id);
              }
            }}
            role="checkbox"
            aria-checked={value.map(String).includes(String(opt.id))}
          >
            <input
              type="checkbox"
              checked={value.map(String).includes(String(opt.id))}
              onChange={() => handleToggle(opt.id)}
              className="hidden"
              tabIndex={-1}
            />
            <span className={
              `flex items-center justify-center w-7 h-7 rounded-full border-2 transition-all duration-150 mr-1 ` +
              (value.map(String).includes(String(opt.id))
                ? 'bg-white border-green-500 text-green-500 shadow-green-200 dark:shadow-green-900'
                : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400')
            }>
              {value.map(String).includes(String(opt.id))
                ? <i className="fas fa-check text-green-500 text-lg" />
                : <i className="fas fa-plus text-gray-400 text-base" />}
            </span>
            <span className="text-base font-semibold text-gray-800 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors whitespace-pre-line">
              {opt.designation}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultiCheckboxSelect;
